import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { media } from "@/lib/api";

export const Logo = ({ className = "h-8 w-8" }) => (
  <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
    <circle cx="50" cy="50" r="31.5" fill="none" stroke="#FF5C00" strokeWidth="23" />
  </svg>
);

export const UserAvatar = ({ user, className = "h-10 w-10", testId }) => {
  const src = media(user?.profile_pic);
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return src ? (
    <img src={src} alt={user?.name} data-testid={testId} className={`${className} rounded-full object-cover bg-stone-200`} />
  ) : (
    <div data-testid={testId} className={`${className} rounded-full bg-[#FF5C00]/10 text-[#C2410C] font-display font-bold grid place-items-center text-sm`}>
      {initials}
    </div>
  );
};

export const Spinner = ({ label = "Loading" }) => (
  <div data-testid="loading-spinner" className="flex items-center gap-2 text-stone-500 py-16 justify-center">
    <Loader2 className="h-5 w-5 animate-spin text-[#FF5C00]" /> <span className="font-mono text-xs uppercase tracking-widest">{label}</span>
  </div>
);

export const Empty = ({ title, children, testId = "empty-state" }) => (
  <div data-testid={testId} className="rounded-3xl border border-dashed border-stone-300 bg-white/60 p-10 text-center">
    <p className="font-display text-xl font-bold">{title}</p>
    {children && <div className="mt-2 text-sm text-stone-500">{children}</div>}
  </div>
);

export const Eyebrow = ({ children }) => (
  <span className="text-[11px] uppercase tracking-[0.2em] font-mono font-semibold text-[#C2410C] bg-orange-50 px-2.5 py-1 rounded-full border border-orange-200/70 inline-flex items-center gap-1.5">
    {children}
  </span>
);

export const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
};

export const Page = ({ children, className = "" }) => (
  <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 pb-28 md:pb-16 ${className}`}>{children}</main>
);
